import type { HTMLAttributes, ReactNode } from 'react';
import { cn } from './cn';

/**
 * Visual style variants for the Badge component.
 */
export type BadgeVariant = 'default' | 'success' | 'warning' | 'error' | 'info';

/**
 * Size options for the Badge component.
 */
export type BadgeSize = 'sm' | 'md';

/**
 * Props for the Badge component.
 */
export interface BadgeProps extends HTMLAttributes<HTMLSpanElement> {
  /**
   * Visual variant controlling color scheme.
   * @default 'default'
   */
  variant?: BadgeVariant;
  /**
   * Size of the badge.
   * @default 'md'
   */
  size?: BadgeSize;
  /** Badge label text. */
  children: ReactNode;
  /** Additional CSS classes. */
  className?: string;
}

const variantClasses: Record<BadgeVariant, string> = {
  default: 'bg-ink/8 text-ink border border-ink/15',
  success: 'bg-okay/15 text-ink border border-okay/25',
  warning: 'bg-warn/15 text-ink border border-warn/25',
  error: 'bg-accent/15 text-ink border border-accent/25',
  info: 'bg-brand/15 text-ink border border-brand/25',
};

const sizeClasses: Record<BadgeSize, string> = {
  sm: 'px-1.5 py-0.5 text-xs',
  md: 'px-2.5 py-0.5 text-sm',
};

/**
 * Compact status indicator badge with semantic color variants.
 *
 * @param props.variant - Color scheme (default | success | warning | error | info)
 * @param props.size - Badge size (sm | md)
 * @example
 * ```tsx
 * <Badge variant="success">Active</Badge>
 * <Badge variant="error" size="sm">Failed</Badge>
 * ```
 */
export function Badge({
  variant = 'default',
  size = 'md',
  className,
  children,
  ...props
}: BadgeProps) {
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full font-medium',
        variantClasses[variant],
        sizeClasses[size],
        className,
      )}
      {...props}
    >
      {children}
    </span>
  );
}
